import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { showErrors, showNotification } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppSimpleDistrictSelector from '@/components/AppSimpleDistrictSelector.vue'
import AppRefreshAndExport from '@/components/AppRefreshAndExport.vue'
import AppSearchBtn from '@/components/AppSearchBtn.vue'
import AppSimpleSelector from '@/components/AppSimpleSelector.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import ListPage from '@/mixins/list-page'
import { DealerModel, DealersOrderBy } from '@/interfaces/dealer'
import { exportDealersList, findDealers } from './gql'
import DealerDialog from './script-dialog'
import { TypedGqlError } from '@/interfaces/error'
import AuthFilter from '@/mixins/auth-filter'
import { SnackType } from '@/interfaces/snack'
import { downloadBase64 } from '@/helpers/base64-to-file'

@Component({
  components: {
    AppBreadcrumbs,
    AppCheckbox,
    AppRefreshAndExport,
    AppDialog,
    AppSimpleTextField,
    AppSimpleSelector,
    AppDatePicker,
    AppSearchBtn,
    AppSimpleDistrictSelector,
    AppTextarea
  }
})

export default class DealerList extends mixins(DealerDialog, ListPage, AuthFilter) {
  @State(state => state.page.DealerList) cache!: string
  breadcrumbs: Breadcrumb[] = [
    {
      text: '经销商管理'
    },
    {
      text: '经销商'
    }
  ]

  options = {
    sortBy: ['realname'],
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true]
  }

  filter = {
    area: null,
    name: '',
    phone: '',
    statusIndex: -1
  }

  totalNum = 0
  loading = false

  disableConfirm = false

  headers = [
    { text: '姓名', value: 'realname' },
    { text: '手机号(经销商登录账号)', value: 'user.phone' },
    { text: '申请区域', value: 'areas[0].mergerName' },
    { text: '店铺名称', value: 'storeName' },
    { text: '审核时间', value: 'auditDate' },
    { text: '申请时间', value: 'applyDate' },
    { text: '状态', value: 'status' },
    { text: '操作', value: 'actions', sortable: false, align: 'end' }
  ]

  disabledActions = true

  dealers: DealerModel[] = []

  windowSize = {
    w: 0,
    h: 0
  }

  selectedDealers = []
  actionsHeight = 51

  @Watch('options', { deep: true })
  onOptionsChanged () {
    this.getDealers()
  }

  mounted () {
    if (this.getRole() === 'dealer') {
      this.headers.pop()
    }
    this.onResize()
    this.restorePage(this.cache)
    this.getDealers()
  }

  // 刷新
  async refreshList () {
    this.filter = {
      area: null,
      name: '',
      phone: '',
      statusIndex: -1
    }
    this.getDealers()
  }

  async getDealers () {
    const { sortBy, page, itemsPerPage, sortDesc } = this.options
    const { name, phone, statusIndex } = this.filter
    const by = DealersOrderBy[sortBy[0] || 'realname']
    const asc = !sortDesc[0] || false

    this.loading = true
    const order = { by, asc }
    const paging = {
      skip: itemsPerPage * (page - 1),
      take: itemsPerPage
    }
    const where = {
      realname: name || null,
      phone_OF_user: phone || null,
      status_EQ: this.auditCodes[statusIndex] || null
    }
    const result = await this.queryDealersRequest(order, paging, where)

    if (result) {
      this.dealers = result.dealers
      this.totalNum = result.paging.totalCount
    }
    this.loading = false
    this.storePage()
  }

  async queryDealersRequest (order: any, paging: any, where: any) {
    try {
      const result = await this.$apollo.query({
        query: findDealers,
        variables: {
          order,
          paging,
          where
        }

      })
      return result.data.findDealers
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  // 搜索
  async filterQuerySubmit () {
    await this.getDealers()
  }

  // 确认删除
  async deleteConfirm () {
    await this.deleteDealerById()
    await this.getDealers()
  }

  // 确认审核
  async auditConfirm () {
    this.auditDialog.show = false
    switch (this.auditIndex) {
      case 0:
        await this.auditDealer(this.AuditStatus.APPROVED)
        this.getDealers()
        break
      case 1:
        this.denyDialog.show = true
        break
      default:
    }
  }

  async denyConfirm () {
    await this.auditDealer(this.AuditStatus.REJECT)
    this.denyDialog.show = false
    this.getDealers()
  }

  // 详情
  routeToDetail (item: DealerModel) {
    this.targetDealer = item

    this.$router.push({
      name: 'DealerDetail',
      params: {
        id: item.id + '',
        name: item.realname
      }
    })
  }

  auditStatus (status: string) {
    return {
      'u-tag-red': status === 'REJECT',
      'u-tag-orange': status === 'PENDING',
      'u-tag-blue': status === 'APPROVED'
    }
  }

  async exportHandler () {
    showNotification({
      msg: '即将开始下载...',
      type: SnackType.Info
    })
    const { sortBy, sortDesc } = this.options
    const { name, phone, statusIndex } = this.filter
    const by = DealersOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.loading = true
    const order = { by, asc }
    const where = {
      realname: name || null,
      phone_OF_user: phone || null,
      status_EQ: this.auditCodes[statusIndex] || null
    }
    try {
      const result = await this.$apollo.query({
        query: exportDealersList,
        variables: {
          order,
          where
        }
      })
      if (result.data) {
        const { fileName, contentB64 } = result.data.exportDealersList
        downloadBase64(contentB64, fileName)
      }
      this.loading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
    const actionDom = this.$refs.actions as Vue & {
      clientHeight: number;
    }
    this.actionsHeight = actionDom.clientHeight
  }
}
